/**
 * Template download component for the asset metadata upload process
 */
import { Download as DownloadIcon } from '@/constants/hardfin-standard-icons';
import { PRODUCT_FIELD_TYPE_DATA } from '@/constants/product-field-type';
import { ItemDetail, ItemField } from '@/items/api/types';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback } from 'react';

interface TemplateDownloadProps {
  selectedItem: ItemDetail;
  onBack: () => void;
  onNext: () => void;
}

/**
 * Renders the template download step of the asset metadata upload process
 */
export function TemplateDownload({
  selectedItem,
  onBack,
  onNext,
}: TemplateDownloadProps) {
  /**
   * Generates and downloads a CSV template based on the selected item
   */
  const handleDownloadTemplate = useCallback(() => {
    if (!selectedItem) return;

    const fields = selectedItem.fields.sort((a, b) => a.order - b.order);
    const headers = [
      'Asset serial',
      ...fields.map((field) => `${field.label}`),
    ];

    const csvContent = headers.join(',') + '\n';

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `${selectedItem.name.toLowerCase().replace(/[^a-z0-9]+/g, '_')}_metadata_template_${DateTime.now().toFormat('yyyy-MM-dd_HHmm')}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [selectedItem]);
  return (
    <Card variant={'outlined'}>
      <CardContent>
        <Box
          sx={{
            px: 3,
            py: 2,
            backgroundColor: (theme) => theme.palette.grey.A100,
            borderRadius: '10px',
            marginBottom: '10px',
          }}
        >
          <Typography variant="subtitle1" gutterBottom>
            Template for {selectedItem.name}
          </Typography>
          <Typography variant="subtitle2" gutterBottom>
            Required
          </Typography>
          <ul style={{ paddingLeft: '24px', marginTop: '8px' }}>
            <li>
              <Typography variant="body2">Asset serial - text</Typography>
            </li>
          </ul>
          <Typography variant="subtitle2" gutterBottom>
            Available item fields
          </Typography>
          <Box
            sx={{
              maxHeight: '250px',
              overflowY: 'auto',
              position: 'relative',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.2)',
                borderRadius: '4px',
              },
              '&:hover::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.3)',
              },
              // Firefox
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(0,0,0,0.2) rgba(0,0,0,0.05)',
            }}
          >
            <ul style={{ paddingLeft: '24px', marginTop: 0, marginBottom: 0 }}>
              {selectedItem.fields
                .sort((a, b) => a.order - b.order)
                .map((field: ItemField) => (
                  <li key={field.id}>
                    <Typography variant="body2">
                      {field.label} -{' '}
                      {PRODUCT_FIELD_TYPE_DATA[
                        field.type
                      ].fieldTypeLabel.toLowerCase()}
                    </Typography>
                  </li>
                ))}
            </ul>
          </Box>
        </Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Important information about the template</AlertTitle>
          <ul style={{ paddingLeft: '24px', marginTop: '8px' }}>
            <li>
              Any fields included in your upload will overwrite existing data,
              including empty cells
            </li>
            <li>
              You can delete columns for fields you don't need to update (only
              "Asset serial" is required)
            </li>
            <li>
              For targeted updates, only include the specific serials and fields
              you want to modify
            </li>
          </ul>
        </Alert>

        <Box display="flex" justifyContent="space-between" mt={2}>
          <Button variant="outlined" onClick={onBack}>
            Back
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
          >
            Download template
          </Button>
          <Button variant="contained" onClick={onNext}>
            Next
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
